/* ==========================================================================
   where-we-deliver.js
   Postcode checker + FAQ accordion for the Where We Deliver page.

   The checker is the engine of the page. It answers four things for one
   address: tier, the exact cut-off for that suburb today, how long is left,
   and any caveat that applies.

   Two rules this file exists to keep:
     1. Nothing here hard-codes a delivery time or a fee. Every number comes
        from wwd-coverage.json, which is generated from the ops coverage file
        by scripts/build-wwd-coverage.js.
     2. The clock is the delivery suburb's, never the browser's. Someone
        ordering from Perth to a Sydney address is judged on Sydney time.
   ========================================================================== */

(function () {
  'use strict';

  var TICK_MS = 30000;

  /* ------------------------------------------------------------- utilities */

  function esc(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function fmtTime(hour) {
    var suffix = hour >= 12 ? 'PM' : 'AM';
    var h = hour % 12 === 0 ? 12 : hour % 12;
    return h + ':00 ' + suffix;
  }

  function fmtFee(value) {
    return '$' + String(value).replace(/^\$/, '');
  }

  function plural(n, word) {
    return n + ' ' + word + (n === 1 ? '' : 's');
  }

  var DOW = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

  // Current weekday/hour/minute in the delivery suburb's timezone.
  function localParts(tz) {
    var parts = new Intl.DateTimeFormat('en-AU', {
      timeZone: tz,
      hour12: false,
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit'
    }).formatToParts(new Date());

    var out = {};
    parts.forEach(function (p) { out[p.type] = p.value; });

    return {
      dow: DOW[out.weekday],
      h: parseInt(out.hour, 10) % 24,
      m: parseInt(out.minute, 10)
    };
  }

  function isWeekend(dow) { return dow === 0 || dow === 6; }

  /* ---------------------------------------------------------------- checker */

  function Checker(root, config) {
    this.root = root;
    this.config = config;
    this.form = root.querySelector('[data-wwd-form]');
    this.input = root.querySelector('[data-wwd-input]');
    this.out = root.querySelector('[data-wwd-out]');
    this.biz = root.querySelector('[data-wwd-business]');

    this.list = root.querySelector('[data-wwd-suggest]');

    this.data = null;
    this.loading = null;
    this.submitted = null;   // postcode currently answered
    this.suburb = null;      // suburb the answer is pinned to, if any
    this.choice = 0;
    this.timer = null;
    this.index = null;       // lazily built suggestion index
    this.options = [];
    this.active = -1;

    this.bind();
    this.renderIdle();
  }

  Checker.prototype.bind = function () {
    var self = this;

    this.form.addEventListener('submit', function (e) {
      e.preventDefault();
      self.submit(self.input.value);
    });

    this.input.addEventListener('input', function () {
      var v = self.input.value.trim();
      self.suburb = null;
      self.suggest(v);
      // A full postcode answers itself as they type, as it always has. Suburb
      // text waits for them to pick one - "Bal" is not an address yet.
      if (self.submitted === null) return;
      if (/^\d{4}$/.test(v)) self.submit(v);
      else self.clear();
    });

    this.input.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown') { e.preventDefault(); self.move(1); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); self.move(-1); return; }
      if (e.key === 'Escape') { self.closeSuggestions(); return; }
      if (e.key === 'Enter' && self.active > -1) {
        e.preventDefault();
        self.choose(self.options[self.active]);
      }
    });

    this.input.addEventListener('blur', function () {
      // Let a click on an option land before the list goes away.
      window.setTimeout(function () { self.closeSuggestions(); }, 150);
    });

    this.list.addEventListener('mousedown', function (e) {
      var opt = e.target.closest('[data-wwd-opt]');
      if (!opt) return;
      e.preventDefault();
      self.choose(self.options[parseInt(opt.getAttribute('data-wwd-opt'), 10)]);
    });

    if (this.biz) {
      this.biz.addEventListener('change', function () {
        if (self.submitted !== null) self.render();
      });
    }

    this.out.addEventListener('click', function (e) {
      var chip = e.target.closest('[data-wwd-try]');
      if (chip) {
        self.input.value = chip.getAttribute('data-wwd-try');
        self.submit(chip.getAttribute('data-wwd-try'));
        self.input.focus();
        return;
      }
      var pick = e.target.closest('[data-wwd-pick]');
      if (pick) {
        self.choice = parseInt(pick.getAttribute('data-wwd-pick'), 10);
        self.render();
        return;
      }
      if (e.target.closest('[data-wwd-reset]')) {
        self.input.value = '';
        self.clear();
        self.input.focus();
      }
    });

    // Warm the coverage set before anyone types, without blocking the page.
    var warm = function () { self.load(); };
    this.input.addEventListener('focus', warm, { once: true });
    if ('requestIdleCallback' in window) window.requestIdleCallback(warm, { timeout: 4000 });
    else window.setTimeout(warm, 2000);
  };

  Checker.prototype.load = function () {
    var self = this;
    if (this.loading) return this.loading;
    this.loading = fetch(this.config.dataUrl)
      .then(function (r) {
        if (!r.ok) throw new Error('coverage ' + r.status);
        return r.json();
      })
      .then(function (json) {
        self.data = json;
        return json;
      });
    return this.loading;
  };

  Checker.prototype.clear = function () {
    this.submitted = null;
    this.suburb = null;
    this.choice = 0;
    this.stopTicking();
    this.renderIdle();
  };

  // "3000", "Carlton, VIC 3053", or a bare suburb name.
  Checker.prototype.resolve = function (text) {
    if (/^\d{4}$/.test(text)) return { pc: text, name: null };

    var tagged = text.match(/^(.+?),\s*[A-Za-z]{2,3}\s*(\d{4})$/);
    if (tagged) return { pc: tagged[2], name: tagged[1].trim() };

    var q = text.toLowerCase();
    var hits = (this.buildIndex() || []).filter(function (e) { return e.key === q; });
    if (hits.length === 1) return { pc: hits[0].pc, name: hits[0].name };
    // One name, several postcodes - let them say which rather than guessing.
    if (hits.length > 1) return 'ambiguous';
    return null;
  };

  Checker.prototype.submit = function (raw, suburb) {
    var self = this;
    var text = String(raw == null ? '' : raw).trim();

    if (!this.data) {
      this.out.innerHTML = '<p class="wwd-checker__sub">Checking…</p>';
      this.load().then(function () {
        self.submit(text, suburb);
      }).catch(function () {
        self.out.innerHTML =
          "<p class=\"wwd-invalid\">We couldn't load the delivery areas just now. Give it another go, or email " +
          '<a href="mailto:' + esc(self.config.email) + '">' + esc(self.config.email) + "</a> and we'll check it for you.</p>";
      });
      return;
    }

    var target = suburb ? { pc: text, name: suburb } : this.resolve(text);

    if (target === 'ambiguous') {
      this.suggest(text);
      return;
    }

    this.stopTicking();

    if (!target) {
      // Not a postcode and not a place we know - say so, keeping their words.
      this.submitted = text;
      this.suburb = null;
      this.render();
      return;
    }

    if (target.pc !== this.submitted || (target.name || null) !== this.suburb) this.choice = 0;
    this.submitted = target.pc;
    this.suburb = target.name || null;
    this.render();
  };

  /* --------------------------------------------------------- suggestions */

  // One flat list of every suburb in the country, covered or not, built once
  // on first use. It is deliberately not limited to the delivery set: someone
  // in Alice Springs has to be able to find their suburb and be told plainly
  // that we cannot reach it yet.
  Checker.prototype.buildIndex = function () {
    if (this.index || !this.data) return this.index;

    var data = this.data;
    var ranges = data.st || [];
    var stateOf = function (pc) {
      var n = parseInt(pc, 10);
      for (var i = 0; i < ranges.length; i++) {
        if (n >= ranges[i][0] && n <= ranges[i][1]) return ranges[i][2];
      }
      return '';
    };

    var index = [];
    var push = function (pc, names, state) {
      names.split('|').forEach(function (name) {
        index.push({ pc: pc, name: name, state: state, key: name.toLowerCase() });
      });
    };

    Object.keys(data.pc).forEach(function (pc) {
      data.pc[pc].forEach(function (g) { push(pc, g[1], data.rules[g[0]].s); });
    });
    Object.keys(data.xs || {}).forEach(function (pc) {
      push(pc, data.xs[pc], stateOf(pc));
    });

    index.sort(function (a, b) { return a.key < b.key ? -1 : a.key > b.key ? 1 : 0; });

    // A second view keyed by postcode, holding the name we lead with for each.
    // Half-typed digits should offer postcodes in numeric order, not whichever
    // suburb happens to sort first alphabetically across the whole range.
    var byPc = {};
    Object.keys(data.pc).forEach(function (pc) {
      byPc[pc] = { pc: pc, name: data.pc[pc][0][1].split('|')[0], state: data.rules[data.pc[pc][0][0]].s };
    });
    Object.keys(data.xs || {}).forEach(function (pc) {
      if (!byPc[pc]) byPc[pc] = { pc: pc, name: data.xs[pc].split('|')[0], state: stateOf(pc) };
    });
    this.postcodes = Object.keys(byPc).sort();
    this.byPc = byPc;

    this.index = index;
    return index;
  };

  var MAX_OPTIONS = 8;

  Checker.prototype.search = function (query) {
    var index = this.buildIndex();
    if (!index) return [];
    var q = query.trim().toLowerCase();
    if (!q) return [];

    // Digits. A partial number offers postcodes in order; a complete one opens
    // up into the suburbs it covers, so a shared postcode can still be pinned
    // down to the right suburb.
    if (/^\d+$/.test(q)) {
      if (q.length < 4) {
        var self = this;
        return this.postcodes
          .filter(function (pc) { return pc.indexOf(q) === 0; })
          .slice(0, MAX_OPTIONS)
          .map(function (pc) { return self.byPc[pc]; });
      }
      return index.filter(function (e) { return e.pc === q; }).slice(0, MAX_OPTIONS);
    }

    if (q.length < 2) return [];

    // Letters: names that start with the query first, then names that merely
    // contain it, so "wood" still finds Burwood without burying Woodend.
    var starts = [];
    var contains = [];
    for (var i = 0; i < index.length; i++) {
      var e = index[i];
      var at = e.key.indexOf(q);
      if (at === 0) starts.push(e);
      else if (at > 0) contains.push(e);
      if (starts.length >= MAX_OPTIONS) break;
    }
    return starts.concat(contains).slice(0, MAX_OPTIONS);
  };

  Checker.prototype.suggest = function (query) {
    var self = this;
    if (!this.data) {
      // Nothing to search yet - fetch, then show what they have typed by now.
      this.load().then(function () { self.suggest(self.input.value); }).catch(function () {});
      return;
    }

    var options = this.search(query);
    this.options = options;
    this.active = -1;

    if (!options.length) {
      this.closeSuggestions();
      return;
    }

    this.list.innerHTML = options.map(function (o, i) {
      return '<li class="wwd-suggest__opt" id="wwd-opt-' + i + '" role="option" aria-selected="false" data-wwd-opt="' + i + '">' +
        '<span class="wwd-suggest__name">' + esc(o.name) + '</span>' +
        '<span class="wwd-suggest__meta">' + esc(o.state) + ' ' + esc(o.pc) + '</span></li>';
    }).join('');

    this.list.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
    this.input.removeAttribute('aria-activedescendant');
  };

  Checker.prototype.closeSuggestions = function () {
    this.list.hidden = true;
    this.list.innerHTML = '';
    this.options = [];
    this.active = -1;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
  };

  Checker.prototype.move = function (step) {
    if (!this.options.length) {
      this.suggest(this.input.value);
      return;
    }
    var next = this.active + step;
    if (next < 0) next = this.options.length - 1;
    if (next >= this.options.length) next = 0;
    this.active = next;

    var items = this.list.querySelectorAll('[data-wwd-opt]');
    for (var i = 0; i < items.length; i++) {
      var on = i === next;
      items[i].setAttribute('aria-selected', on ? 'true' : 'false');
      items[i].classList.toggle('is-active', on);
      if (on && items[i].scrollIntoView) items[i].scrollIntoView({ block: 'nearest' });
    }
    this.input.setAttribute('aria-activedescendant', 'wwd-opt-' + next);
  };

  Checker.prototype.choose = function (option) {
    if (!option) return;
    this.input.value = option.name + ', ' + option.state + ' ' + option.pc;
    this.closeSuggestions();
    this.submit(option.pc, option.name);
  };

  /* ------------------------------------------------------------- the lookup */

  Checker.prototype.groups = function (pc) {
    var entry = this.data && this.data.pc[pc];
    if (!entry) return null;
    var rules = this.data.rules;
    return entry.map(function (g) {
      var names = g[1].split('|');
      return { rule: rules[g[0]], primary: names[0], names: names };
    });
  };

  // Everything the result card needs, for one postcode and, where they named
  // one, one suburb inside it.
  Checker.prototype.answer = function (pc, suburb) {
    if (!/^\d{4}$/.test(pc)) return { kind: 'invalid', typed: pc };

    var groups = this.groups(pc);
    if (!groups) return { kind: 'none', pc: pc, place: suburb || pc };

    // A named suburb answers for itself. Where a postcode spans two runs the
    // suburb settles which one, so there is nothing left to ask about.
    var pinned = -1;
    if (suburb) {
      for (var i = 0; i < groups.length; i++) {
        if (groups[i].names.indexOf(suburb) !== -1) { pinned = i; break; }
      }
    }

    var group = groups[pinned > -1 ? pinned : Math.min(this.choice, groups.length - 1)];
    var rule = group.rule;
    var business = !!(this.biz && this.biz.checked);

    var base = {
      pc: pc,
      groups: groups,
      group: group,
      // Suburbs the ops file does not name still sit in a covered postcode, so
      // they take that postcode's rule and keep their own name on the card.
      place: (suburb || group.primary) + ', ' + rule.s,
      hidePicker: !!suburb,
      area: rule.a || 'your area',
      fee: fmtFee(rule.f),
      noBusiness: rule.nb === 1,
      business: business
    };

    // The 11 AM school/business cut-off only means something where we can
    // deliver to a school or business at all. In the three areas we can't, an
    // 11 AM time next to "we can't deliver there" would contradict itself, so
    // the times shown stay the home-address ones and the banner does the work.
    var applyBusiness = business && rule.nb !== 1;

    var now = localParts(rule.z);
    var weekend = isWeekend(now.dow);
    var cut = weekend ? rule.we : rule.wd;
    if (applyBusiness) cut = Math.min(cut, 11);
    var minsLeft = (cut - now.h) * 60 - now.m;

    var tomorrowWeekend = isWeekend((now.dow + 1) % 7);
    var nextCut = tomorrowWeekend ? rule.we : rule.wd;
    if (applyBusiness) nextCut = Math.min(nextCut, 11);

    base.cutoff = fmtTime(cut);
    base.nextCutoff = fmtTime(nextCut);
    base.minsLeft = minsLeft;

    if (rule.t === 'tas') return Object.assign(base, { kind: 'tas', place: group.primary + ', TAS' });
    if (rule.t === 'next') return Object.assign(base, { kind: 'next', passed: minsLeft <= 0 });

    if (minsLeft <= 0) return Object.assign(base, { kind: 'passed' });

    var hrs = Math.floor(minsLeft / 60);
    var mins = minsLeft % 60;
    base.timeLeft = hrs > 0 ? plural(hrs, 'hour') + ' ' + plural(mins, 'minute') : plural(mins, 'minute');
    return Object.assign(base, { kind: 'live' });
  };

  /* ----------------------------------------------------------------- render */

  Checker.prototype.renderIdle = function () {
    var chips = this.config.suggestions || [];
    if (!chips.length) {
      this.out.innerHTML = '';
      return;
    }
    this.out.innerHTML =
      '<div class="wwd-chips"><span class="wwd-chips__label">Try one:</span>' +
      chips.map(function (c) {
        return '<button type="button" class="wwd-chip" data-wwd-try="' + esc(c.postcode) + '">' +
          esc(c.postcode + ' ' + c.label) + '</button>';
      }).join('') +
      '</div>';
  };

  Checker.prototype.shopButton = function (label, url) {
    return '<a class="wwd-btn wwd-btn--pink" href="' + esc(url) + '">' + esc(label) + '</a>';
  };

  Checker.prototype.picker = function (r) {
    if (r.hidePicker || r.groups.length < 2) return '';
    var current = Math.min(this.choice, r.groups.length - 1);
    return '<div class="wwd-pick">' +
      '<p class="wwd-pick__label">' + esc(r.pc) + ' covers suburbs on different runs. Which one is it going to?</p>' +
      '<div class="wwd-pick__opts">' +
      r.groups.map(function (g, i) {
        var extra = g.names.length > 1 ? ' +' + (g.names.length - 1) : '';
        return '<button type="button" class="wwd-pick__opt" data-wwd-pick="' + i + '" aria-pressed="' +
          (i === current ? 'true' : 'false') + '">' + esc(g.primary + extra) + '</button>';
      }).join('') +
      '</div></div>';
  };

  Checker.prototype.caveats = function (r) {
    var out = '';
    if (r.noBusiness) {
      out += '<p class="wwd-banner wwd-banner--amber">Home addresses only around here - we can\'t deliver to schools or businesses in ' +
        esc(r.area) + '.</p>';
    } else if (r.business) {
      out += '<p class="wwd-banner wwd-banner--amber">Going to a school or an office? That cut-off is 11:00 AM, wherever it lands.</p>';
    }
    return out;
  };

  Checker.prototype.render = function () {
    var r = this.answer(this.submitted || '', this.suburb);
    var cfg = this.config;
    var html;

    if (r.kind === 'invalid') {
      // Two different mistakes, two different nudges: a wrong-length number is
      // a typo, whereas unrecognised words need pointing at the suggestions.
      html = /^\d+$/.test(r.typed || '')
        ? '<p class="wwd-invalid">Australian postcodes are four digits - mind checking that one?</p>'
        : "<p class=\"wwd-invalid\">We don't know that one - try a postcode, or start typing a suburb and pick it from the list.</p>";
      this.stopTicking();
    } else if (r.kind === 'none') {
      html =
        '<div class="wwd-result">' +
        '<h3 class="wwd-result__title">We don\'t have a driver near ' + esc(r.place) + ' yet.</h3>' +
        '<p style="margin-top:18px">Email ' + esc(cfg.email) + ' with the address and we\'ll quote it - we can often make something work even when the map says no.</p>' +
        '<div class="wwd-result__actions">' +
        '<a class="wwd-btn wwd-btn--pink" href="mailto:' + esc(cfg.email) + '">Email us →</a>' +
        '<button type="button" class="wwd-btn wwd-btn--ghost" data-wwd-reset>Check another postcode</button>' +
        '</div></div>';
      this.stopTicking();
    } else if (r.kind === 'live') {
      html =
        '<div class="wwd-result">' +
        '<div class="wwd-result__head"><span class="wwd-result__tick wwd-result__tick--same" aria-hidden="true">✓</span>' +
        '<h3 class="wwd-result__title">' + esc(r.place) + ' - we can get it there today.</h3></div>' +
        '<div class="wwd-tiles">' +
        '<div class="wwd-tile wwd-tile--time"><div class="wwd-tile__label">Time left today</div>' +
        '<div class="wwd-tile__value">' + esc(r.timeLeft) + '</div></div>' +
        '<div class="wwd-tile"><div class="wwd-tile__label">Cut-off for ' + esc(r.area) + '</div>' +
        '<div class="wwd-tile__value">' + esc(r.cutoff) + '</div></div>' +
        '</div>' +
        '<p>Order before ' + esc(r.cutoff) + ' and it arrives between 11am and 9pm tonight.</p>' +
        '<p class="wwd-result__note">Delivery <strong>' + esc(r.fee) + '</strong> - a dedicated floral driver, and a tracking link when it\'s on the way. ' +
        '<a href="#' + esc(cfg.feeAnchor) + '"><strong>Why? →</strong></a></p>' +
        this.caveats(r) +
        '<div class="wwd-result__actions">' + this.shopButton(cfg.shopLabel, cfg.shopUrl) + '</div>' +
        this.picker(r) +
        '</div>';
      this.startTicking();
    } else if (r.kind === 'passed') {
      html =
        '<div class="wwd-result">' +
        '<div class="wwd-result__head"><span class="wwd-result__tick wwd-result__tick--same" aria-hidden="true">✓</span>' +
        '<h3 class="wwd-result__title">' + esc(r.place) + ' - same-day delivery area.</h3></div>' +
        '<p style="margin-top:18px">Today\'s cut-off (' + esc(r.cutoff) + ') has been and gone, but tomorrow\'s wide open. ' +
        'Order now and choose tomorrow at checkout - tomorrow\'s cut-off is ' + esc(r.nextCutoff) + '.</p>' +
        this.caveats(r) +
        '<div class="wwd-result__actions">' + this.shopButton(cfg.shopLabel, cfg.shopUrl) + '</div>' +
        this.picker(r) +
        '</div>';
      this.startTicking();
    } else if (r.kind === 'next') {
      var dispatch = r.passed
        ? 'Today\'s ' + esc(r.cutoff) + ' dispatch cut-off has passed, so it leaves us tomorrow. 98% arrive the next working day after that.'
        : 'Order by ' + esc(r.cutoff) + ' and it leaves us today. 98% arrive the next working day.';
      html =
        '<div class="wwd-result">' +
        '<div class="wwd-result__head"><span class="wwd-result__tick wwd-result__tick--next" aria-hidden="true">✓</span>' +
        '<h3 class="wwd-result__title">' + esc(r.place) + ' - next-day delivery.</h3></div>' +
        '<p style="margin-top:18px">' + dispatch + ' It travels in the flower box we engineered ourselves - one has survived a drop from a second-floor window with the posy still intact.</p>' +
        '<p class="wwd-result__note">Delivery <strong>' + esc(r.fee) + '</strong>. ' +
        '<a href="#' + esc(cfg.feeAnchor) + '"><strong>What that covers →</strong></a></p>' +
        '<p class="wwd-banner wwd-banner--blue">Heads up: national deliveries arrive Monday to Friday, and plants and our largest arrangements can\'t travel this far.</p>' +
        '<div class="wwd-result__actions">' + this.shopButton(cfg.shopLabel, cfg.shopUrl) + '</div>' +
        this.picker(r) +
        '</div>';
      this.startTicking();
    } else {
      html =
        '<div class="wwd-result">' +
        '<div class="wwd-result__head"><span class="wwd-result__tick wwd-result__tick--tas" aria-hidden="true">✓</span>' +
        '<h3 class="wwd-result__title">' + esc(r.place) + ' - gifts yes, flowers no.</h3></div>' +
        '<p style="margin-top:18px">Tasmania\'s biosecurity rules mean we can\'t send fresh flowers or plants across the strait. ' +
        'Everything else we make - hampers, dessert boxes, champagne, personalised gifts - travels just fine. ' +
        'Order by ' + esc(r.cutoff) + ' for next-day dispatch.</p>' +
        '<div class="wwd-result__actions">' + this.shopButton(cfg.tasLabel, cfg.tasUrl) + '</div>' +
        this.picker(r) +
        '</div>';
      this.stopTicking();
    }

    this.out.innerHTML = html;
  };

  Checker.prototype.startTicking = function () {
    var self = this;
    if (this.timer) return;
    this.timer = window.setInterval(function () {
      if (self.submitted) self.render();
    }, TICK_MS);
  };

  Checker.prototype.stopTicking = function () {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = null;
  };

  /* --------------------------------------------------------------- accordion */

  function initFaq(root) {
    root.querySelectorAll('[data-wwd-faq-trigger]').forEach(function (trigger) {
      var panel = document.getElementById(trigger.getAttribute('aria-controls'));
      if (!panel) return;
      trigger.addEventListener('click', function () {
        var open = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', open ? 'false' : 'true');
        panel.hidden = open;
        var sign = trigger.querySelector('[data-wwd-faq-sign]');
        if (sign) sign.textContent = open ? '+' : '–';
      });
    });
  }

  /* -------------------------------------------------------------------- init */

  function init() {
    document.querySelectorAll('[data-wwd-root]').forEach(function (root) {
      var configEl = root.querySelector('[data-wwd-config]');
      if (configEl && root.querySelector('[data-wwd-form]')) {
        try {
          new Checker(root, JSON.parse(configEl.textContent));
        } catch (err) {
          if (window.console) console.error('[wwd] checker failed to start', err);
        }
      }
      initFaq(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
